import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
    Typography,
    SimplePopover,
    TertiaryButton,
    CustomTableRowsProps,
    DotsIcon,
    TableRowNameLink,
    TemplatePreview,
} from '@dev/ee-components';
import { getPaidNewslettersStore, LoadPreviewTemplate } from '@/store/paidNewsletters/actions';
import { DropdownOptions } from '@/store/paidNewsletters/types';
import { AccessLevel, Newsletter, SimpleNewsletter } from '@dev/ee-api/ee-api-models';
import { getApiPath } from '@/services/Secuirty';
import { push } from 'redux-first-history';
import { PaidNewslettersPaths, pathsParams } from '@/services/dictionaries/Paths';
import { useAccessLevel } from '@/services/hooks/useAccessLevel';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { StatusBadge } from '@/assets/StatusBadge';
import { useLoadingPaidNewsletters } from '../useLoadingPaidNewsletters';
import { useDropdownActions } from '../useDropdownActions';


export const useTableRows = (setPreviewContent): CustomTableRowsProps[] => {
    const { t } = useTranslation();
    const paidNewsletters = useSelector(getPaidNewslettersStore);
    const dispatch = useDispatch();
    const { getDropdownActions } = useDropdownActions();
    const { hasAccessLevel } = useAccessLevel();
    const canModifyPaidNewsletters = hasAccessLevel(AccessLevel.ModifyPaidNewsletters);
    useLoadingPaidNewsletters(paidNewsletters)

    const [previewClicked, setPreviewClicked] = useState(false);

    useEffect(() => {
        if (!paidNewsletters.previewLoading && previewClicked) {
            onPreviewClick(paidNewsletters.paidNewsletter);
            setPreviewClicked(false)
        }
    }, [paidNewsletters.previewLoading]); 

    const onPreviewClick = (paidNewsletter: Newsletter): void => {
        if(!paidNewsletter?.ProductLanding.Template.Body.Parts){
            return
        }
        setPreviewContent({ content: paidNewsletter?.ProductLanding.Template.Body.Parts[0].Content, name: paidNewsletter?.Name });
    };

    const handlePreviewClickCurried = (paidNewsletter) => (paidNewsletterTemplatesThumbnail) => {
        if (!paidNewsletterTemplatesThumbnail) return;      
        dispatch(LoadPreviewTemplate(paidNewsletter.Name));     
        setPreviewClicked(true)
};

    return paidNewsletters?.templatesList.map((paidNewsletter: SimpleNewsletter, index): CustomTableRowsProps => {
        const rowPopoverOptions = [
            {
                label: canModifyPaidNewsletters ? t('editpaidnewsletter') : t('show'),
                value: DropdownOptions.EditPaidNewsletter,
            },
            ...(canModifyPaidNewsletters ? [{
                label: t('delete'),
                color: '#ff0000',
                value: DropdownOptions.Delete,
            }] : []),
        ];

        const paidNewsletterTemplatesThumbnail = paidNewsletter?.ProductScreenshot
        ? `${getApiPath()}/userfile${paidNewsletter.ProductScreenshot}`
        : '';

        const handlePopoverItemClick = (ev: React.MouseEvent, option: { label: string; value: DropdownOptions }): void => {
            ev.stopPropagation();
            getDropdownActions(option, paidNewsletter);
        };

        return {
            paidNewsletterPreview: (
                <div style={{ marginBottom: '20px', marginTop: '20px' }}
                >
                    <TemplatePreview
                        split={false}
                        screenshot={paidNewsletterTemplatesThumbnail}
                        onClick={() => handlePreviewClickCurried(paidNewsletter)(paidNewsletterTemplatesThumbnail)}
                        disabled={!paidNewsletterTemplatesThumbnail}
                    />
                </div>
            ),
            paidNewsletterName: (
                <div style={{ wordBreak: 'break-all', paddingRight: '10px' }}>
                    <Typography variant="caption">
                        <TableRowNameLink onClick={() => getDropdownActions({ value: DropdownOptions.EditPaidNewsletter }, paidNewsletter)}>
                            {paidNewsletter.Name}
                        </TableRowNameLink>
                    </Typography>
                </div>
            ),
            status: (
                <StatusBadge key={`status-${index}`} value={t('draft') as string} status="Draft" borderDisabled />
            ),
            icon:  (
                <div style={{display:'inline-flex', gap:'24px'}}>
                    {canModifyPaidNewsletters && <TertiaryButton 
                        text={t('edit')} 
                        onClick={(): void => {
                            dispatch(
                                push(
                                    PaidNewslettersPaths.PaidNewsletterCreator.replace(
                                        pathsParams.nameOptional,
                                        safelyEncodeBase64Url(paidNewsletter.Name)
                                    )
                                )
                            );
                        }}
                    />}
                    <div
                        onClick={(ev): void => {
                            ev.stopPropagation();
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <SimplePopover
                            type="icon"
                            options={rowPopoverOptions}
                            enableCheckbox={false}
                            placement={"bottom-start"}
                            onItemClick={handlePopoverItemClick}
                        >
                            <DotsIcon />
                        </SimplePopover>
                    </div>
                </div>
            ),
            rowID: paidNewsletter.Name,
        };
    });
};